import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import UpgradePrompt from '../components/UpgradePrompt.jsx';
import { HelpTooltip } from '../components/EnhancedTooltip.jsx';
import ParrotStatusIndicator from '../components/ParrotStatusIndicator.jsx';
import KnowledgeBaseSyncStatusModal from '../components/KnowledgeBaseSyncStatusModal.jsx';
import { ApiClient } from '../utils/apiClient';

export default function ParrotsList() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const [parrots, setParrots] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState({
    isOpen: false,
    message: '',
    upgradeOptions: null,
    showTopupOption: false,
    minimumBalance: 0
  });
  
  // Phone number filtering state
  const [filterByPhoneNumbers, setFilterByPhoneNumbers] = useState('all'); // 'all', 'assigned', 'unassigned'
  const [hoveredParrot, setHoveredParrot] = useState(null);
  
  // KB sync status modal state
  const [kbStatusModal, setKbStatusModal] = useState({
    isOpen: false,
    parrotId: null,
    parrotName: null
  });

  // Fetch parrots and voices from API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [parrotsData, voicesData] = await Promise.all([
        apiClient.get('/api/parrots'),
        apiClient.get('/api/voices')
      ]);
      
      // Fetch phone numbers for each parrot
      const parrotsWithPhoneNumbers = await Promise.all(
        parrotsData.map(async (parrot) => {
          try {
            const phoneNumbers = await apiClient.get(`/api/parrots/${parrot.id}/phone-numbers`);
            return {
              ...parrot,
              phone_numbers: phoneNumbers.phone_numbers || []
            };
          } catch (error) {
            console.warn(`Failed to fetch phone numbers for parrot ${parrot.id}:`, error);
            return {
              ...parrot,
              phone_numbers: []
            };
          }
        })
      );
      
      setParrots(parrotsWithPhoneNumbers);
      setVoices(voicesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  // Delete parrot
  const handleDeleteParrot = async (parrotId) => {
    try {
      setDeleting(true);
      
      await apiClient.delete(`/api/parrots/${parrotId}`);

      // Remove parrot from local state
      setParrots(parrots.filter(p => p.id !== parrotId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting parrot:', err);
      
      // Handle payment required error
      if (err.status === 402) {
        try {
          const errorData = await err.response?.json();
          setUpgradePrompt({
            isOpen: true,
            message: errorData?.error || err.message,
            upgradeOptions: errorData?.upgrade_options || null,
            showTopupOption: errorData?.topup_required || false,
            minimumBalance: errorData?.minimum_balance || 0
          });
          setDeleteConfirm(null);
          return;
        } catch (parseError) {
          // Fall through to general error handling
        }
      }
      
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };



  // Filter parrots based on phone number assignment status
  const filteredParrots = parrots.filter(parrot => {
    const hasPhoneNumbers = parrot.phone_numbers && parrot.phone_numbers.length > 0;
    
    switch (filterByPhoneNumbers) {
      case 'assigned':
        return hasPhoneNumbers;
      case 'unassigned':
        return !hasPhoneNumbers;
      default:
        return true;
    }
  });

  // Format phone numbers for display
  const formatPhoneNumbers = (phoneNumbers) => {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      return 'No phone numbers assigned';
    }
    
    if (phoneNumbers.length === 1) {
      return phoneNumbers[0].friendly_name || phoneNumbers[0].number;
    }
    
    return `${phoneNumbers.length} phone numbers assigned`;
  };

  // Get phone numbers tooltip content
  const getPhoneNumbersTooltip = (phoneNumbers) => {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      return 'No phone numbers assigned to this parrot';
    }
    
    return phoneNumbers.map(phone => 
      `${phone.friendly_name || phone.number}${phone.assigned_at ? ` (assigned ${formatDate(phone.assigned_at)})` : ''}`
    ).join('\n');
  };

  // Load data when component mounts
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      fetchData();
    }
  }, [isLoaded, isSignedIn, fetchData]);

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">My Parrots</h1>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Manage your AI voice parrots</p>
          

        </div>
        <Link 
          to="/parrots/new/step1" 
          className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-medium inline-flex items-center dark:text-white"
        >
          <span className="mr-2">+</span>
          Create New Parrot
        </Link>
      </div>

      {/* Filter Controls */}
      {!loading && parrots.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Filter by phone numbers:</span>
              <HelpTooltip
                content="Filter Parrots based on their phone number assignments. 'With phone numbers' shows Parrots that have at least one phone number assigned. 'Without phone numbers' shows Parrots that need phone number assignments to be reachable."
                position="top"
              />
            </div>
            <select
              value={filterByPhoneNumbers}
              onChange={(e) => setFilterByPhoneNumbers(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All parrots</option>
              <option value="assigned">With phone numbers</option>
              <option value="unassigned">Without phone numbers</option>
            </select>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
            Showing {filteredParrots.length} of {parrots.length} parrots
          </div>
        </div>
      )}
      

      
      {error && (
            <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-6">
              <p className="font-medium">Error loading parrots</p>
              <p className="text-sm">{error}</p>
              <button 
                onClick={fetchData}
                className="mt-2 text-sm underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          )}

      {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading your parrots...</p>
              </div>
            </div>
          ) : parrots.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-center py-12">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Parrots Yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 dark:text-gray-300 dark:text-gray-600">Create your first AI voice parrot to get started.</p>
                <Link 
                  to="/parrots/new/step1" 
                  className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-medium inline-block dark:text-white"
                >
                  Create New Parrot
                </Link>
              </div>
            </div>
          ) : filteredParrots.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-center py-12">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Parrots Match Filter</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 dark:text-gray-300 dark:text-gray-600">
                  {filterByPhoneNumbers === 'assigned' 
                    ? 'No parrots have phone numbers assigned yet.'
                    : filterByPhoneNumbers === 'unassigned'
                    ? 'All parrots have phone numbers assigned.'
                    : 'No parrots found.'
                  }
                </p>
                <button
                  onClick={() => setFilterByPhoneNumbers('all')}
                  className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline"
                >
                  Show all parrots
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredParrots.map((parrot) => (
                <div 
                  key={parrot.id} 
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors relative dark:bg-gray-900"
                  onMouseEnter={() => setHoveredParrot(parrot.id)}
                  onMouseLeave={() => setHoveredParrot(null)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 pr-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                        {parrot.name}
                      </h3>
                      {/* Phone number count badge and Knowledge Base status */}
                      <div className="mt-1 flex items-center space-x-2">
                        {parrot.phone_numbers && parrot.phone_numbers.length > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/20 text-teal-800 dark:text-teal-300">
                            📞 {parrot.phone_numbers.length} number{parrot.phone_numbers.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        <ParrotStatusIndicator
                          parrotId={parrot.id}
                          parrotName={parrot.name}
                          size="small"
                          showProgress={true}
                          showTooltip={true}
                          onClick={(event, data) => {
                            event.stopPropagation();
                            setKbStatusModal({
                              isOpen: true,
                              parrotId: parrot.id,
                              parrotName: parrot.name
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Link
                        to={`/parrots/${parrot.id}/edit/step1`}
                        className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 p-1"
                        title="Edit parrot"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => setDeleteConfirm(parrot.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                        title="Delete parrot"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Voice:</span>
                      <span>{parrot.voice_name || 'Default Voice'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-500 dark:text-gray-400">Phone Numbers:</span>
                        <HelpTooltip
                          content={
                            parrot.phone_numbers && parrot.phone_numbers.length > 0
                              ? `This Parrot handles ${parrot.phone_numbers.length} phone number${parrot.phone_numbers.length !== 1 ? 's' : ''}. All numbers use the same AI configuration but provide local presence in different areas.`
                              : "This Parrot has no phone numbers assigned yet. Assign phone numbers to make this Parrot reachable by callers."
                          }
                          position="top"
                        />
                      </div>
                      <span className={parrot.phone_numbers && parrot.phone_numbers.length > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}>
                        {formatPhoneNumbers(parrot.phone_numbers)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>
                      <span>{formatDate(parrot.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Modified:</span>
                      <span>{formatDate(parrot.updated_at)}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Link
                      to={`/parrots/${parrot.id}/edit/step1`}
                      className="block w-full text-center bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg font-medium transition-colors dark:text-white"
                    >
                      Edit Parrot
                    </Link>
                  </div>

                  {/* Phone Numbers Tooltip */}
                  {hoveredParrot === parrot.id && parrot.phone_numbers && parrot.phone_numbers.length > 0 && (
                    <div className="absolute z-10 top-full left-0 mt-2 p-3 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg shadow-lg max-w-xs dark:text-white">
                      <div className="font-medium mb-2">Assigned Phone Numbers:</div>
                      <div className="space-y-1">
                        {parrot.phone_numbers.map((phone, index) => (
                          <div key={index} className="flex flex-col">
                            <span className="font-medium">{phone.friendly_name || phone.number}</span>
                            {phone.assigned_at && (
                              <span className="text-xs text-gray-300 dark:text-gray-400 dark:text-gray-600">
                                Assigned: {formatDate(phone.assigned_at)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Tooltip arrow */}
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-700 transform rotate-45"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Delete Parrot</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 dark:text-gray-600">
              Are you sure you want to delete this parrot? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 dark:text-white dark:text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteParrot(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 dark:text-white"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt */}
      <UpgradePrompt
        isOpen={upgradePrompt.isOpen}
        onClose={() => setUpgradePrompt({ ...upgradePrompt, isOpen: false })}
        message={upgradePrompt.message}
        upgradeOptions={upgradePrompt.upgradeOptions}
        showTopupOption={upgradePrompt.showTopupOption}
        minimumBalance={upgradePrompt.minimumBalance}
      />

      {/* Knowledge Base Sync Status Modal */}
      <KnowledgeBaseSyncStatusModal
        isOpen={kbStatusModal.isOpen}
        onClose={() => setKbStatusModal({ isOpen: false, parrotId: null, parrotName: null })}
        parrotId={kbStatusModal.parrotId}
        parrotName={kbStatusModal.parrotName}
      />
    </div>
  );
}
